import { NumericFormat, NumericFormatProps } from "@sunbound/react-number-format";

interface MoneyInputProps extends Omit<NumericFormatProps, `onValueChange` | `customInput`> {
    onValueChange: (value: string) => void;
    as?: NumericFormatProps[`customInput`];
    error?: string | string[];
    containerClassName?: string;
    className?: string;
}

/**
 * MoneyInput component for entering monetary values with automatic formatting.
 *
 * Features:
 * - Dollar sign prefix
 * - Thousands separator (comma)
 * - Two decimal places
 * - No negative values allowed
 * - Automatic formatting as you type
 * - Can be rendered with custom component via 'as' prop (e.g., as={TextField})
 *
 * @example
 * ```tsx
 * // Basic usage
 * <MoneyInput
 *   value={amount}
 *   onValueChange={(value) => setAmount(value)}
 *   placeholder="Enter amount"
 *   className="my-input-class"
 * />
 *
 * // With TextField component
 * <MoneyInput
 *   as={TextField}
 *   value={amount}
 *   onValueChange={(value) => setAmount(value)}
 *   placeholder="Enter amount"
 *   error={errors.amount}
 * />
 * ```
 */
export const MoneyInput: React.FC<MoneyInputProps> = ({
    onValueChange,
    as,
    ...props
}) => (
    <NumericFormat
        type="text"
        prefix="$"
        thousandSeparator=","
        decimalSeparator="."
        allowNegative={false}
        decimalScale={2}
        customInput={as}
        isAllowed={(values) => {
            const { floatValue } = values;
            return floatValue === undefined || floatValue >= 0;
        }}
        formatPartialValue={(inputValue): string | void => {
            if (inputValue.trim().startsWith(`$`)) return `$`;
        }}
        onValueChange={(values) => {
            onValueChange(values.value);
        }}
        {...props}
    />
);
